import { createArray, findIndex } from "@fluentui/react/lib/Utilities";

import type { TabHeader } from "./TabList.Props";

/**
 * Is responsible for splitting the current set of tabs into top level and overflow tabs. This method assumes that all contextual tabs
 * appear in order in the tabHeaders list and that they appear at the end of the tabHeaders list.
 */
export function splitTabHeadersIntoOverflow(
  tabHeaders: TabHeader[],
  overflowItemCount: number,
  selectedTabId: string
): { topLevelTabHeaders: TabHeader[]; overflowTabHeaders: TabHeader[] } {
  /* The tab header scaling rules are as follows:
   * 1. The currently selected tab is always in the top level
   * 2. Contextual tabs are the last tabs to go into the overflow well
   * 3. All tabs in the overflow well appear in their original order
   */

  const selectedTabIndex = findIndex(
    tabHeaders,
    (tabHeader) => tabHeader.id === selectedTabId
  );

  if (selectedTabIndex < 0) {
    /* eslint-disable-next-line no-console */
    console.warn(`No tab with id: ${selectedTabId} was found`);
    return { topLevelTabHeaders: [], overflowTabHeaders: [] };
  }

  // Assign a priority to each tabHeader. The priorities are in the range [0, tabHeaders.length - 1]
  // Elements with lower priorities should drop into an overflow well before items with a higher priority
  const priorities: number[] = createArray(tabHeaders.length, (_) => -1);
  let currentPriority = tabHeaders.length - 1;

  const assignHighestAvailablePriorityToElements = (
    startIndex: number,
    endIndex?: number
  ) => {
    let endIndexToUse = endIndex;
    if (endIndexToUse === undefined) {
      endIndexToUse = startIndex;
    }

    for (let index = startIndex; index <= endIndexToUse; index += 1) {
      if (priorities[index] === -1) {
        priorities[index] = currentPriority;
        currentPriority -= 1;
      }
    }
  };

  // The selected tab has the highest priority
  assignHighestAvailablePriorityToElements(selectedTabIndex);

  // Contextual tabs have the next highest priority, in left to right order
  // Note that in cases where add-ins are installed contextual tabs needn't be the last tabs.
  tabHeaders.forEach((item, index) => {
    if (item.tabType === "contextual") {
      assignHighestAvailablePriorityToElements(index);
    }
  });

  // Lastly, the remaining tabs get the leftover priority in left to right order
  assignHighestAvailablePriorityToElements(0, tabHeaders.length - 1);

  return {
    topLevelTabHeaders: tabHeaders.filter(
      (_, index) => priorities[index] >= overflowItemCount
    ),
    overflowTabHeaders: tabHeaders.filter(
      (_, index) => priorities[index] < overflowItemCount
    ),
  };
}
